import { Popover } from "@mui/material";

import type { FilterDefinition } from "./FilterDefinition";
import styles from "./FilterMenu.module.css";
import { FilterMenuBody } from "./FilterMenuBody";
import type { Filter } from "./FilterSchema";

export interface FilterMenuProps {
  /** Whether the menu is currently open. */
  open: boolean;
  /** Element the popover anchors to (the trigger button). */
  anchorEl: HTMLElement | null;
  /**
   * Called when the user clicks outside the menu, presses Escape, or
   * commits a filter (in which case `onCommit` fires first). The caller
   * is responsible for transitioning their popup state to closed.
   */
  onClose: () => void;
  /** Filterable fields the menu offers in its picker. */
  filterDefinitions: FilterDefinition[];
  /**
   * Fired when the user commits a single configured filter. `onClose`
   * fires immediately after.
   */
  onCommit: (filter: Filter) => void;
}

/**
 * Anchored popover containing the field picker + filter form. Shared
 * between `<DataTable.Filters>` (the labeled outlined button in the
 * Actions row) and `<DataTable.ActiveFilters>`'s inline `+` button so
 * both triggers open the same menu and route through the same commit
 * handler. The caller owns the trigger element and the open/anchor state
 * — typically via `usePopupState` — and passes them in as primitives.
 */
export function FilterMenu({
  open,
  anchorEl,
  onClose,
  filterDefinitions,
  onCommit,
}: FilterMenuProps) {
  const handleCommit = (filter: Filter) => {
    onCommit(filter);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          className: styles.popover,
          role: "dialog",
          "aria-label": "Filter menu",
        },
      }}
    >
      <FilterMenuBody
        filterDefinitions={filterDefinitions}
        onCommit={handleCommit}
        onCancel={onClose}
      />
    </Popover>
  );
}
